import CommercialRegister, { SegmentedControl } from "@/components/CommercialRegister";
// Quotes list page. Office only. Send to customer via PDF email.
import { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/pages/dashboard/_shared";
import { Button } from "@/components/ui/button";
import { Plus, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { QuoteImportDialog } from "@/components/QuoteImportDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";

const STATUS = {
  draft: { label: "Draft", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  sent: { label: "Sent", cls: "bg-sky-100 text-sky-800 border-sky-400" },
  accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-800 border-emerald-400" },
  declined: { label: "Declined", cls: "bg-zinc-200 text-zinc-700 border-zinc-400" },
  expired: { label: "Expired", cls: "bg-amber-100 text-amber-800 border-amber-400" },
  converted: { label: "→ Invoice", cls: "bg-violet-100 text-violet-800 border-violet-400" },
};

const TABS = [
  { key: "draft", label: "Drafts" },
  { key: "sent", label: "Sent" },
  { key: "accepted", label: "Accepted" },
  { key: "declined", label: "Rejected" },
  { key: "converted", label: "Invoiced" },
  { key: "all", label: "All" },
];

export default function Quotes() {
  const [items, setItems] = useState([]);
  const requestRef = useRef(null);
  const [tab, setTab] = useState("draft");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importInitialFile, setImportInitialFile] = useState(null);
  const [pageDragging, setPageDragging] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    requestRef.current?.abort();
    const controller = new AbortController(); requestRef.current = controller;
    setLoading(true); setLoadError(false);
    try {
      const { data } = await api.get("/quotes", { params: { status: tab }, signal: controller.signal });
      if (!controller.signal.aborted) setItems(data || []);
    } catch {
      if (!controller.signal.aborted) { setLoadError(true); setItems([]); }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => requestRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const openNew = () => navigate("/app/quotes/new");
  const openEdit = (q) => navigate(`/app/quotes/${q.id}`);

  const duplicate = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/duplicate`);
      toast.success(`Duplicated as ${data.quote_number} (draft)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const copyClientLink = async (q) => {
    const url = `${window.location.origin}/q/${q.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Client link copied — they can view & accept online");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const del = async (q) => {
    if (!window.confirm(`Delete quote ${q.quote_number}? Can't be undone.`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      setItems((p) => p.filter((x) => x.id !== q.id));
      toast.success("Quote deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const markAccepted = async (q) => {
    if (!window.confirm(`Mark quote ${q.quote_number} as accepted?`)) return;
    try {
      await api.post(`/quotes/${q.id}/respond`, { action: "accept" });
      toast.success(`${q.quote_number} marked as accepted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please give a reason for rejecting this quote");
      return;
    }
    setRejectBusy(true);
    try {
      await api.post(`/quotes/${rejecting.id}/respond`, { action: "decline", reason: rejectReason.trim() });
      toast.success(`${rejecting.quote_number} marked as rejected`);
      setRejecting(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setRejectBusy(false);
    }
  };

  const convert = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert-to-invoice`);
      toast.success(`Converted to invoice ${data.invoice_number}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    }
  };

  const convertToJob = async (q) => {
    if (!window.confirm(`Convert quote ${q.quote_number} to a live job? Line items will be copied to the job's cost breakdown.`)) return;
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert-to-job`);
      toast.success(`Job ${data.job?.job_number} created from quote`);
      load();
      // Jump straight into the new job so the office can dispatch it
      if (data.job?.id) navigate(`/app/jobs/${data.job.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    }
  };

  const onImportCreated = (q) => {
    setItems((prev) => [q, ...prev.filter((x) => x.id !== q.id)]);
    // Jump to the drafts tab so the office can immediately see + edit it
    if (tab !== "draft" && tab !== "all") setTab("draft");
  };

  return (
    <div
      data-testid="quotes-page"
      onDragOver={(e) => {
        // Only intercept if the drag actually contains files (not e.g. text selection)
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setPageDragging(true);
      }}
      onDragLeave={(e) => {
        // Ignore leave events fired when moving between child elements
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setPageDragging(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setPageDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (!f) return;
        const ok = /\.(xlsx|xls|csv)$/i.test(f.name);
        if (!ok) {
          toast.error("Drop an .xlsx or .csv Schedule of Quantities");
          return;
        }
        setImportInitialFile(f);
        setImportOpen(true);
      }}
      className="relative px-4 py-4 md:p-6 lg:p-6 w-full pb-24 lg:pb-10"
    >
      {pageDragging && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center pointer-events-none" data-testid="quotes-page-drop-overlay">
          <div className="bg-white dark:bg-[#151C27] border-4 border-dashed border-[#FF5F15] rounded-sm p-10 text-center max-w-md mx-auto">
            <UploadCloud className="w-12 h-12 text-[#FF5F15] mx-auto mb-3" />
            <div className="font-display font-black uppercase tracking-tight text-xl">Drop your SOQ</div>
            <div className="text-[12px] text-zinc-500 mt-1">We&apos;ll parse it into a draft quote</div>
          </div>
        </div>
      )}

      <PageHeader
        tag="/ Sales"
        title="Quotes"
        subtitle="Prepare quotes, track acceptance and turn approved work into jobs."
        action={
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Button
              onClick={() => { setImportInitialFile(null); setImportOpen(true); }}
              variant="outline"
              className="rounded-lg border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 uppercase tracking-wider font-bold text-xs flex-1 md:flex-none"
              data-testid="quote-import-open"
            >
              <UploadCloud className="w-3.5 h-3.5 mr-1.5" /> Import SOQ
            </Button>
            <Button onClick={openNew} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs flex-1 md:flex-none" data-testid="quote-new">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New quote
            </Button>
          </div>
        }
      />

      <div className="mb-4"><SegmentedControl label="Quote status" options={TABS} value={tab} onChange={setTab} testId="quotes-tabs" /></div>
      <CommercialRegister kind="quote" items={items} loading={loading} error={loadError} onRetry={load} statuses={STATUS} onOpen={openEdit}
        extra={q => <>
          {q.doc_type === 'estimate' && <span className="block text-xs text-sky-700 mt-1">Estimate</span>}
          {q.imported_from_file && <span className="block text-xs text-muted-foreground mt-1">Imported from spreadsheet</span>}
          {q.converted_job_id && <button className="block text-xs font-medium text-orange-700 mt-1" onClick={() => navigate(`/app/jobs/${q.converted_job_id}`)}>View linked job →</button>}
          {q.status === 'declined' && (q.declined_reason || q.acceptance?.comment) && <p className="text-xs text-red-600 mt-1">{q.declined_reason || q.acceptance.comment}</p>}
        </>}
        actions={q => [
          {label:'Open PDF', href:`${api.defaults.baseURL}/quotes/${q.id}/pdf`, testId:`quote-pdf-${q.id}`},
          {label:'Duplicate as draft', onClick:() => duplicate(q), testId:`quote-duplicate-${q.id}`},
          q.public_token && {label:'Copy customer link', onClick:() => copyClientLink(q), testId:`quote-link-${q.id}`},
          !['accepted','converted'].includes(q.status) && {label:'Mark accepted', onClick:() => markAccepted(q), testId:`quote-accept-${q.id}`},
          !['declined','converted'].includes(q.status) && !q.converted_invoice_id && !q.converted_job_id && {label:'Reject quote', onClick:() => {setRejecting(q);setRejectReason('');}, testId:`quote-reject-${q.id}`},
          q.status === 'accepted' && !q.converted_invoice_id && {label:'Convert to invoice', onClick:() => convert(q), testId:`quote-convert-${q.id}`},
          q.status === 'accepted' && !q.converted_job_id && {label:'Convert to job', onClick:() => convertToJob(q), testId:`quote-convert-job-${q.id}`},
          {label:'Delete quote', onClick:() => del(q), destructive:true, testId:`quote-del-${q.id}`},
        ]}
      />

      <QuoteImportDialog
        open={importOpen}
        onClose={() => { setImportOpen(false); setImportInitialFile(null); }}
        onCreated={onImportCreated}
        initialFile={importInitialFile}
      />

      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setRejectReason(""); } }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="quote-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight">Reject quote {rejecting?.quote_number}</DialogTitle>
            <DialogDescription>
              {rejecting?.client_name ? `${rejecting.client_name} — ` : ""}give a reason so the team knows why this one didn&apos;t land.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Went with another contractor — our price was too high"
            rows={3}
            className="rounded-sm border-zinc-300"
            data-testid="quote-reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setRejectReason(""); }} className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="quote-reject-cancel">
              Cancel
            </Button>
            <Button onClick={submitReject} disabled={rejectBusy || !rejectReason.trim()} className="rounded-sm bg-red-600 hover:bg-red-700 text-white uppercase tracking-wider font-bold text-xs" data-testid="quote-reject-confirm">
              {rejectBusy ? "Rejecting…" : "Reject quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
